import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  InternalContainer,
  ExternalContainer,
} from "../../components/Reusable/Layout";
import { UserDetailModal } from "../../components/userManagement/userDetailModal";
import { UsersTable, UserSearchFilter } from "./component";
import {
  fetchUsers,
  fetchFilterOptions,
  setFilters,
  clearFilters,
} from "../../features/auth/store/userSlice";
import {
  fetchCountries,
  selectCountries,
} from "../../features/auth/store/countrySlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { useEffect } from "react";
import { downloadUsersCsv } from "../../features/auth/api/userExport";

const UserManagement = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [exporting, setExporting] = useState(false);

  const handleUserClick = (user: any) => {
    setSelectedUser(user);
    setIsModalOpen(true);
    setSelectedUserId(user.user_id);
  };

  const dispatch = useAppDispatch();
  const { token, user: authenticatedAdmin } = useAppSelector(
    (state) => state.auth,
  );
  const countries = useAppSelector(selectCountries) || [];
  const {
    users,
    loading,
    error,
    total,
    perPage,
    currentPage,
    lastPage,
    filters,
    filterOptions,
  } = useAppSelector((state) => state.users);
  const canExportUsers = ["super_admin", "admin"].includes(
    String(authenticatedAdmin?.role || "").toLowerCase(),
  );

  const handleFilterChange = useCallback(
    (field: string, value: string) => {
      const updatedFilters = {
        ...filters,
        [field]: value,
      };

      dispatch(setFilters(updatedFilters));
      dispatch(fetchUsers({ ...updatedFilters, page: 1, per_page: 15 }));
    },
    [dispatch, filters],
  );

  useEffect(() => {
    dispatch(clearFilters());
    dispatch(fetchUsers({ page: 1, per_page: 15 }));
    dispatch(fetchFilterOptions());
    dispatch(fetchCountries({ perPage: 250 }));
  }, [dispatch]);

  const handlePageChange = (newPage: number) => {
    if (!loading && newPage >= 1 && newPage <= lastPage) {
      dispatch(fetchUsers({ ...filters, page: newPage, per_page: 15 }));
    }
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(fetchUsers({ page: 1, per_page: 15 }));
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleExportUsers = async () => {
    if (!token) {
      toast.error("Your session has expired. Please sign in again.");
      return;
    }

    setExporting(true);

    try {
      await downloadUsersCsv(filters, token);
      toast.success("User data downloaded successfully.");
    } catch {
      toast.error("Unable to download user data.");
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <ExternalContainer>
        <div className="w-full px-4 sm:px-6 md:px-0 dark:bg-zinc-950">
          <InternalContainer headerText="User Management">
            <div className="flex items-center justify-center h-64 dark:bg-zinc-900 rounded-lg p-4 text-center">
              <div>
                <p className="text-red-500 text-lg mb-4">Error loading users</p>
                <p className="text-gray-500 dark:text-zinc-400 mb-4">{error}</p>
                <button
                  onClick={() =>
                    dispatch(fetchUsers({ page: 1, per_page: 15 }))
                  }
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </InternalContainer>
        </div>
      </ExternalContainer>
    );
  }

  return (
    <ExternalContainer>
      <div className="h-auto w-full min-h-screen dark:bg-zinc-950 transition-colors duration-300 max-w-6xl mx-auto px-4 py-6">
        <InternalContainer headerText="User Management">
          <div className="flex flex-col dark:bg-zinc-950 gap-4 w-full">
            <UserSearchFilter
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              filters={filters}
              filterOptions={filterOptions}
              countries={countries}
              loading={loading}
              onExport={canExportUsers ? handleExportUsers : undefined}
              exporting={exporting}
            />
            <div className="border border-[#FFFFFF1A] dark:bg-zinc-900 rounded-lg overflow-x-auto w-full">
              <UsersTable
                users={users}
                onUserClick={handleUserClick}
                loading={loading}
                currentPage={currentPage}
                totalPages={lastPage}
                onPageChange={handlePageChange}
                total={total}
                perPage={perPage}
              />
            </div>
          </div>
        </InternalContainer>
      </div>
      {selectedUser && (
        <UserDetailModal
          userId={selectedUserId}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          showTabs={true}
          tabItems={["Personal Information", "Gamer Tags", "Purchases"]}
        />
      )}
    </ExternalContainer>
  );
};

export default UserManagement;
